import { getGetUsersQueryKey, PaginatedUserListItemsDTO, UserListItemDTO, useDeleteUserById, useGetUsers } from "@bvhi/backend-api";
import { Box, Card, CardContent, GridPaginationModel, GridSortModel, Typography } from "@bvhi/ui-base";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { forwardRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { BVHIDataGrid } from "../../../../../../../components/datagrid/bvhi-datagrid";
import { SecuredDeleteDialog } from "../../../../../../../dialogs/secured-delete-dialog";
import { useToast } from "../../../../../../../hooks/use-toast";
import { Route } from "../../../../../../../routes/_main/app/users.index";
import { toNarrowedTypeOrNull } from "../../../../../../../utils/ts";
import { useUsersDataGridDefinitions } from "../../hooks/use-users-data-grid-definitions";
import { useUsersSortParams } from "../../hooks/use-users-sort-params";

interface Props {
  updateCallback: (paginationModel: GridPaginationModel, sortModel: GridSortModel) => void;
}

export const UsersDataGrid = forwardRef<HTMLDivElement, Props>(({ updateCallback }, ref) => {
  const searchParams = Route.useSearch();
  const { t } = useTranslation(["users", "dialogs"]);
  const queryClient = useQueryClient();
  const { getSortModelForBackendString } = useUsersSortParams();

  const { data, isFetching } = useGetUsers(searchParams, {
    query: { throwOnError: (error) => error.status === 500 || error.status === 403, placeholderData: keepPreviousData },
  });

  const { mutate: deleteUser } = useDeleteUserById();

  const [userToDelete, setUserToDelete] = useState<UserListItemDTO | null>(null);
  const { pushDeleteSuccessToast, pushDeleteErrorToast } = useToast();

  const handleDelete = useCallback((user: UserListItemDTO) => {
    setUserToDelete(user);
  }, []);

  const { columns } = useUsersDataGridDefinitions(handleDelete);

  const handleCornfirmDialogClose = useCallback(
    (confirmed: boolean) => {
      const userId = userToDelete?.id;

      if (!confirmed || !userId) {
        setUserToDelete(null);
        return;
      }
      deleteUser(
        { userId },
        {
          onSuccess: () => {
            pushDeleteSuccessToast(t("users:users.user"));
          },
          onError: () => {
            pushDeleteErrorToast(t("users:users.user"));
          },
          onSettled: () => {
            setUserToDelete(null);
            void queryClient.invalidateQueries({ queryKey: getGetUsersQueryKey() });
          },
        }
      );
    },
    [deleteUser, pushDeleteSuccessToast, pushDeleteErrorToast, queryClient, t, userToDelete]
  );

  const users = toNarrowedTypeOrNull<PaginatedUserListItemsDTO>(data?.data, "numberOfItems");

  const deleteAdditionalContent =
    userToDelete?.isMinion === false ? (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2">{t("dialogs:dialogs.delete.organization-owner.title")}</Typography>
      </Box>
    ) : undefined;

  return (
    <div ref={ref}>
      <Card sx={{ minHeight: 300 }}>
        <CardContent restrictContentWidth={false}>
          <BVHIDataGrid
            columns={columns}
            getRowHeight={() => "auto"}
            initialSortModel={getSortModelForBackendString(searchParams.sort && searchParams.sort.length >= 1 ? searchParams.sort[0] : "")}
            loading={isFetching}
            rowCount={users?.numberOfItems ?? -1}
            rows={users?.payload ?? []}
            totalPages={users?.numberOfPages ?? 0}
            updateCallback={updateCallback}
            initalPaginationModel={{
              page: searchParams.page ? searchParams.page : 0,
              pageSize: searchParams.size ? searchParams.size : 25,
            }}
          />
        </CardContent>
      </Card>
      <SecuredDeleteDialog
        onClose={handleCornfirmDialogClose}
        open={userToDelete !== null}
        overwrite={{
          title: t("dialogs:dialogs.delete.user.title"),
          additionalContent: deleteAdditionalContent,
        }}
      />
    </div>
  );
});
UsersDataGrid.displayName = "UsersDataGrid";
